import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaDumbbell,
  FaEye,
  FaGift,
  FaMedal,
  FaRocket,
  FaStar,
  FaTrophy,
  FaUserCheck,
  FaUserFriends,
} from "react-icons/fa";
import { GiKimono, GiPodiumWinner } from "react-icons/gi";

import useScrollToTop from "./../../hooks/useScollToTop";

const JUDOKA_FEATURES = [
  {
    id: "profil_judoka",
    icon: FaUserCheck,
    eyebrow: "Profil Judoka",
    title: "Profil Judoka",
    subtitle: "Compléter sa fiche sportive",
    description:
      "Le judoka dispose d’une fiche personnelle afin de définir ses informations, son club, son âge, sa catégorie de poids et d'âge.",
    image: "/img/diaporama/Page_Adherent.png",
    accent: "from-sky-500 to-blue-600",
    soft: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    ring: "border-sky-200 dark:border-sky-500/20",
    bullets: [
      "Informations personnelles et sportives",
      "Club, âge, catégorie et ceinture",
      "Données importantes du judoka",
    ],
  },
  {
    id: "competitions",
    icon: FaCalendarAlt,
    eyebrow: "Compétitions",
    title: "Calendrier & inscriptions",
    subtitle: "Voir les événements et suivre sa saison",
    description:
      "Le judoka peut consulter les compétitions, stages ou événements partagés, retrouver les informations importantes et suivre les inscriptions liées à sa saison sportive.",
    image: "/img/diaporama/Page_Mes_Competitions.png",
    accent: "from-sky-500 to-blue-600",
    soft: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    ring: "border-sky-200 dark:border-sky-500/20",
    bullets: [
      "Calendrier des compétitions et événements",
      "Détails pratiques : date, lieu, niveau, catégorie",
      "Possibilité de copier des événements partagés par les autres utilisateurs",
    ],
  },
  {
    id: "results",
    icon: FaTrophy,
    eyebrow: "Résultats",
    title: "Résultats & palmarès",
    subtitle: "Garder une trace de ses performances",
    description:
      "Le judoka retrouve ses résultats, ses podiums, ses classements et son historique de compétition pour visualiser son évolution tout au long de la saison.",
    image: "/img/diaporama/Page_Palmares.png",
    accent: "from-amber-500 to-orange-500",
    soft: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
    ring: "border-amber-200 dark:border-amber-500/20",
    bullets: [
      "Historique des compétitions",
      "Podiums, classements et résultats",
      "Palmarès personnel clair et lisible",
    ],
  },
  {
    id: "progression",
    icon: FaMedal,
    eyebrow: "Progression",
    title: "Ceintures, grades & objectifs",
    subtitle: "Visualiser sa progression technique",
    description:
      "L’espace progression permet au judoka de suivre son grade actuel, son historique de ceintures et les UV et points pour la ceinture noire.",
    image: "/img/diaporama/Page_Passage_de_grade.png",
    accent: "from-purple-500 to-indigo-600",
    soft: "bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
    ring: "border-purple-200 dark:border-purple-500/20",
    bullets: [
      "Grade actuel et historique des ceintures",
      "Objectifs techniques personnalisés",
      "Suivi de l’évolution dans la saison",
    ],
  },
  {
    id: "revisions",
    icon: FaBookOpen,
    eyebrow: "Révisions",
    title: "Techniques, katas, lexique & quiz",
    subtitle: "Réviser son judo à son rythme",
    description:
      "Le judoka peut réviser les techniques, consulter les katas, travailler le vocabulaire japonais et tester ses connaissances avec des quizz adaptés.",
    image: "/img/diaporama/Page_Techniques.png",
    accent: "from-cyan-500 to-teal-500",
    soft: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-200",
    ring: "border-cyan-200 dark:border-cyan-500/20",
    bullets: [
      "Techniques classées par famille",
      "Katas et vocabulaire japonais",
      "Quiz pour apprendre de manière ludique",
    ],
  },
  
  {
    id: "arbitrage",
    icon: FaUserCheck,
    eyebrow: "Arbitrage",
    title: "Règles d’arbitrage",
    subtitle: "Comprendre les règles et les décisions",
    description:
      "Le module d’arbitrage permet au judoka de comprendre les règles de compétition, les décisions d’arbitrage, les pénalités et les critères de victoire pour mieux préparer ses combats.",
    image: "/img/diaporama/Page_Arbitrage.png",
    accent: "from-emerald-500 to-teal-600",
    soft: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    ring: "border-emerald-200 dark:border-emerald-500/20",
    bullets: [
      "Règlement Arbitrage",
      "Règlement Commissaire Sportif",
      "Quizz Arbitrage",
    ],
  },
];

const FUTURE_FEATURES = [
  {
    icon: GiPodiumWinner,
    title: "Carnet de progression",
    text: "Une évolution possible permettra au judoka de retrouver un vrai carnet de progression avec les techniques à valider, les objectifs donnés par le coach et les étapes vers la prochaine ceinture.",
    gradient: "from-sky-500 to-blue-600",
  },
  {
    icon: FaUserFriends,
    title: "Travail avec partenaires",
    text: "Une future fonctionnalité pourra aider les judokas à trouver des partenaires pour travailler les katas, préparer une compétition ou organiser des entraînements ciblés.",
    gradient: "from-purple-500 to-indigo-600",
  },
  {
    icon: FaStar,
    title: "Défis & objectifs",
    text: "L’espace judoka pourra évoluer avec des défis techniques, physiques ou théoriques pour rendre la progression plus motivante au fil de la saison.",
    gradient: "from-amber-500 to-orange-500",
  },
];

function ScreenshotPreview({ src, alt, activeFeature }) {
  const [hasError, setHasError] = useState(false);
  const PreviewIcon = activeFeature.icon;

  if (hasError || !src) {
    return (
      <div className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-[2rem] border border-dashed border-slate-300 bg-slate-100 text-center dark:border-white/10 dark:bg-white/[0.04]">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${activeFeature.accent} opacity-10`}
        />

        <div className="relative px-6">
          <FaEye className="mx-auto text-3xl text-slate-400 dark:text-white/35" />
          <p className="mt-3 text-sm font-bold text-slate-600 dark:text-white/60">
            Capture d’écran à ajouter
          </p>
          <p className="mt-1 text-xs font-medium text-slate-400 dark:text-white/35">
            Format conseillé : paysage 16/9
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white p-2 shadow-2xl shadow-slate-300/70 dark:border-white/10 dark:bg-white/10 dark:shadow-black/30">
      <div
        className={`absolute -inset-8 bg-gradient-to-br ${activeFeature.accent} opacity-20 blur-3xl`}
      />

      <div className="relative overflow-hidden rounded-[1.6rem] bg-slate-100 dark:bg-slate-950">
        <img
          src={src}
          alt={alt}
          onError={() => setHasError(true)}
          className="aspect-[16/9] w-full object-cover"
        />

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 to-transparent p-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white backdrop-blur-xl">
            <PreviewIcon />
            Aperçu du module
          </div>
        </div>
      </div>
    </div>
  );
}

function JudoExpert() {
  useScrollToTop();

  const [activeFeatureId, setActiveFeatureId] = useState(
    JUDOKA_FEATURES[0].id
  );

  const activeFeature = useMemo(() => {
    return (
      JUDOKA_FEATURES.find((feature) => feature.id === activeFeatureId) ||
      JUDOKA_FEATURES[0]
    );
  }, [activeFeatureId]);

  const ActiveIcon = activeFeature.icon;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-140px] top-20 h-80 w-80 rounded-full bg-cyan-300/25 blur-3xl dark:bg-cyan-500/10" />
        <div className="absolute right-[-120px] top-[420px] h-96 w-96 rounded-full bg-blue-300/25 blur-3xl dark:bg-blue-500/10" />
        <div className="absolute bottom-20 left-1/3 h-96 w-96 rounded-full bg-purple-300/20 blur-3xl dark:bg-purple-500/10" />
      </div>

      <section className="relative mx-auto w-full max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8">
        <Fade triggerOnce direction="up" duration={700}>
          <div className="relative overflow-hidden rounded-[2.3rem] border border-cyan-300/20 bg-gradient-to-br from-cyan-500 via-sky-600 to-blue-950 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.55)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.20),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.10),transparent_24%)]" />
            <div className="absolute -left-16 top-10 h-44 w-44 rounded-full bg-cyan-200/25 blur-3xl" />
            <div className="absolute right-0 top-0 h-56 w-56 rounded-full bg-blue-200/10 blur-3xl" />

            <div className="relative grid gap-8 p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:p-10">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-cyan-100 backdrop-blur-xl">
                  <GiKimono />
                  Judo Expert
                </div>

                <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                  Un espace gratuit pour suivre sa saison et progresser en
                  judo.
                </h1>

                <p className="mt-5 max-w-2xl text-sm font-medium leading-7 text-blue-50/85 sm:text-base">
                  Judo Expert accompagne chaque judoka connecté : compétitions,
                  résultats, progression, révisions, objectifs et préparation
                  physique.
                </p>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/register"
                    className="inline-flex min-h-[48px] items-center justify-center gap-3 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-xl shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-cyan-50"
                  >
                    Créer mon compte gratuit
                    <FaChevronRight className="text-xs" />
                  </Link>

                  <Link
                    to="/login"
                    className="inline-flex min-h-[48px] items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                  >
                    Déjà inscrit ? Me connecter
                  </Link>
                </div>
              </div>

              <Zoom triggerOnce duration={600}>
                <div className="relative rounded-[2rem] border border-white/20 bg-white/10 p-5 text-white backdrop-blur-xl">
                  <div className="absolute -right-6 -top-6 flex h-20 w-20 items-center justify-center rounded-[1.6rem] border border-white/20 bg-white/15 text-3xl shadow-2xl backdrop-blur-xl">
                    <FaGift />
                  </div>

                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-100">
                    Formule judoka
                  </p>

                  <p className="mt-3 font-heading text-5xl font-bold">
                    0€
                    <span className="ml-2 text-base font-bold text-white/70">
                      / an
                    </span>
                  </p>

                  <p className="mt-3 text-sm font-medium leading-7 text-white/70">
                    L’accès judoka est gratuit pour permettre à chaque pratiquant
                    de suivre sa progression et de profiter des outils essentiels.
                  </p>

                  <div className="mt-5 grid gap-3">
                    {[
                      "Compte judoka gratuit",
                      "Suivi personnel de la saison",
                      "Outils de révision intégrés",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-3 rounded-2xl bg-white/10 p-3"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                          <FaCheckCircle />
                        </div>
                        <p className="text-sm font-bold">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Zoom>
            </div>
          </div>
        </Fade>
      </section>

      <section className="relative mx-auto w-full max-w-[1280px] px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          <Slide triggerOnce direction="left" duration={650}>
            <aside className="lg:sticky lg:top-[120px] lg:self-start">
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/30">
                <div
                  className={`bg-gradient-to-br ${activeFeature.accent} p-5 text-white`}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                    Fonctionnalités judoka
                  </p>

                  <h2 className="mt-2 font-heading text-2xl font-bold">
                    Choisissez un module
                  </h2>
                </div>

                <div className="space-y-2 p-4">
                  {JUDOKA_FEATURES.map((feature) => {
                    const Icon = feature.icon;
                    const isActive = activeFeatureId === feature.id;

                    return (
                      <button
                        key={feature.id}
                        type="button"
                        onClick={() => setActiveFeatureId(feature.id)}
                        className={`group flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                          isActive
                            ? `${feature.ring} ${feature.soft} shadow-lg`
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:-translate-y-0.5 hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] dark:text-white/70 dark:hover:bg-white/[0.08]"
                        }`}
                      >
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition ${
                            isActive
                              ? "bg-white/70 text-current shadow-sm dark:bg-white/10"
                              : "bg-white text-slate-500 group-hover:text-sky-600 dark:bg-white/10 dark:text-white/45"
                          }`}
                        >
                          <Icon />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold">
                            {feature.title}
                          </p>
                          <p className="truncate text-xs font-semibold opacity-70">
                            {feature.subtitle}
                          </p>
                        </div>

                        <FaChevronRight
                          className={`shrink-0 text-xs transition ${
                            isActive
                              ? "translate-x-0 text-current"
                              : "text-slate-400 group-hover:translate-x-1 group-hover:text-sky-500 dark:text-white/30"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          </Slide>

          <div className="space-y-6">
            <Fade
              triggerOnce
              direction="up"
              duration={700}
              key={activeFeature.id}
            >
              <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/30">
                <div
                  className={`h-2 bg-gradient-to-r ${activeFeature.accent}`}
                />

                <div className="p-4 sm:p-6">
                  <ScreenshotPreview
                    src={activeFeature.image}
                    alt={activeFeature.title}
                    activeFeature={activeFeature}
                  />

                  <div className="mt-7 grid gap-7 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
                    <div>
                      <Zoom triggerOnce duration={450}>
                        <div
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] ${activeFeature.soft} ${activeFeature.ring}`}
                        >
                          <ActiveIcon />
                          {activeFeature.eyebrow}
                        </div>
                      </Zoom>

                      <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                        {activeFeature.title}
                      </h2>

                      <p className="mt-3 text-lg font-bold text-slate-700 dark:text-white/80">
                        {activeFeature.subtitle}
                      </p>

                      <p className="mt-4 text-sm font-medium leading-8 text-slate-600 dark:text-white/60 sm:text-base">
                        {activeFeature.description}
                      </p>
                    </div>

                    <div className="grid gap-3">
                      {activeFeature.bullets.map((bullet, index) => (
                        <Fade
                          key={bullet}
                          triggerOnce
                          direction="up"
                          delay={index * 80}
                          duration={450}
                        >
                          <div className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${activeFeature.soft}`}
                            >
                              <FaCheckCircle />
                            </div>

                            <p className="text-sm font-bold text-slate-700 dark:text-white/75">
                              {bullet}
                            </p>
                          </div>
                        </Fade>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Fade>

            {/* <Fade triggerOnce direction="up" duration={700}>
              <section className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
                <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
                  <div className="relative overflow-hidden bg-slate-950 p-6 text-white">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(14,165,233,0.35),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(124,58,237,0.25),transparent_30%)]" />

                    <div className="relative">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-cyan-200 backdrop-blur-xl">
                        <FaRocket />
                      </div>

                      <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-cyan-200">
                        Prochaines évolutions
                      </p>

                      <h2 className="mt-2 font-heading text-2xl font-bold">
                        Un espace judoka qui pourra évoluer avec les clubs
                      </h2>

                      <p className="mt-3 text-sm font-medium leading-7 text-white/65">
                        Judo Expert pourra devenir un vrai carnet numérique de
                        progression pour accompagner chaque judoka dans sa saison,
                        ses révisions et ses objectifs.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
                    {FUTURE_FEATURES.map((item, index) => {
                      const Icon = item.icon;

                      return (
                        <Fade
                          key={item.title}
                          triggerOnce
                          direction="up"
                          delay={index * 120}
                        >
                          <div className="h-full rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]">
                            <div
                              className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} text-white shadow-lg`}
                            >
                              <Icon />
                            </div>

                            <p className="mt-3 text-sm font-bold text-slate-950 dark:text-white">
                              {item.title}
                            </p>

                            <p className="mt-1 text-xs font-medium leading-5 text-slate-500 dark:text-white/50">
                              {item.text}
                            </p>
                          </div>
                        </Fade>
                      );
                    })}
                  </div>
                </div>
              </section>
            </Fade> */}
          </div>
        </div>
      </section>
    </main>
  );
}

export default JudoExpert;